/**
 * Stockage du token d'accès. Chaque app utilise sa propre clé
 * (`fidalia.admin.token` / `fidalia.app.token`) pour que les deux
 * interfaces puissent coexister sur le même navigateur sans se marcher dessus.
 */
export type TokenStore = {
  get: () => string | null;
  set: (token: string) => void;
  clear: () => void;
  subscribe: (listener: () => void) => () => void;
};

export type TokenStoreOptions = {
  /**
   * Recopie le jeton dans un cookie de même nom, à côté du `localStorage`.
   *
   * Utile à un seul lecteur : l'extension navigateur. Un `localStorage` n'est
   * lisible que par du code s'exécutant dans une page de l'origine, ce qui
   * obligerait l'extension à garder un onglet de l'app ouvert pour savoir
   * qui est connecté. Un cookie, lui, se lit par `chrome.cookies`, sans
   * onglet, et sa disparition se signale d'elle-même à la déconnexion.
   *
   * L'app, elle, continue de lire le `localStorage` : le cookie est un
   * miroir, jamais la source. Et l'API n'accepte toujours que
   * `Authorization: Bearer` — ce cookie n'authentifie rien, il n'ouvre donc
   * aucune surface CSRF.
   */
  miroirCookie?: boolean;
};

/** Durée de vie du cookie miroir, alignée sur le TTL du jeton (7 jours). */
const COOKIE_MAX_AGE = 7 * 24 * 60 * 60;

function ecrireCookie(nom: string, valeur: string): void {
  // `Secure` casserait le cookie en dev (http://localhost) : il n'est posé
  // que là où il a un sens.
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  globalThis.document.cookie = `${nom}=${valeur}; Path=/; Max-Age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function effacerCookie(nom: string): void {
  const secure = globalThis.location?.protocol === 'https:' ? '; Secure' : '';
  globalThis.document.cookie = `${nom}=; Path=/; Max-Age=0; SameSite=Lax${secure}`;
}

function lireCookie(nom: string): string | null {
  const entrees = globalThis.document?.cookie?.split('; ') ?? [];
  for (const entree of entrees) {
    const separateur = entree.indexOf('=');
    if (separateur > 0 && entree.slice(0, separateur) === nom) return entree.slice(separateur + 1);
  }
  return null;
}

export function createTokenStore(storageKey: string, options: TokenStoreOptions = {}): TokenStore {
  const listeners = new Set<() => void>();
  const notify = () => listeners.forEach((listener) => listener());

  const lire = (): string | null => {
    try {
      return globalThis.localStorage?.getItem(storageKey) ?? null;
    } catch {
      return null;
    }
  };

  const miroir = (token: string | null): void => {
    if (!options.miroirCookie) return;
    try {
      if (token) ecrireCookie(storageKey, token);
      else effacerCookie(storageKey);
    } catch {
      // Cookies refusés : l'app fonctionne sans, seule l'extension y perd.
    }
  };

  // Sessions déjà ouvertes avant l'arrivée du miroir : le cookie manquerait
  // jusqu'à la prochaine connexion. On le rattrape au démarrage.
  if (options.miroirCookie) {
    const existant = lire();
    if (existant && lireCookie(storageKey) !== existant) miroir(existant);
  }

  return {
    get: lire,
    set: (token) => {
      globalThis.localStorage?.setItem(storageKey, token);
      miroir(token);
      notify();
    },
    clear: () => {
      globalThis.localStorage?.removeItem(storageKey);
      miroir(null);
      notify();
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
